import React from 'react'

import { PlotBox } from '../components/chart-segment/PlotBox'
import { ChartHeadingGap, ChartSegmentTitleSlot } from '../components/chart-segment/ChartSegmentTitleSlot'
import { TextClamp } from '../components/text/TextClamp'
import { clampArray } from '../foundations/slide-limits'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { insightBulletRhythm, schemaBackedProseClampProps } from '../foundations/text-overflow-policy'
import { registerSlide } from './registry'
import { RailLayout } from '../layouts/RailLayout'
import { presetSlot } from '../layouts/presetSlots'
import { SlotBullets, SlotHeadline, SlotNarrative } from '../slots'
import { PLOT_CARD_SURFACE } from '../slots/PlotCard'
import { SlideQuoteCard } from './SlideQuoteCard'
import { ChoiceSlideBars, RecordedChoiceSlideBars, renderCutSplit } from './choice-slide-chart'
import { EditableSlideText } from './EditableSlideText'
import type { EditableSlideProps } from './contracts'
import type { StoredChoiceSlide } from '../plots/choice-bars'
import type { SingleSelectBarsSlideData } from './renderer-internal-types'

// legacy reader only: the narrative eyebrow is required, so a deck built today stamps it.
const LEGACY_EYEBROW = 'Single select'

const GRAY = 'var(--slide-text-muted)'
const BRAND_EMPHASIS = 'var(--slide-accent)'
const SINGLE_SELECT_SLIDE_TYPE = 'single-select-bars'
const ACTION_SUBTITLE_CLAMP = schemaBackedProseClampProps('actionSubtitle', SINGLE_SELECT_SLIDE_TYPE, {
  lineHeight: '1.5',
})
const CHART_TITLE_CLAMP = schemaBackedProseClampProps('chartTitle', SINGLE_SELECT_SLIDE_TYPE, {
  lineHeight: '1.2',
})

const HEADLINE = presetSlot(SINGLE_SELECT_SLIDE_TYPE, 'headline').text!

function SingleSelectBarsSlideComponent(props: SingleSelectBarsSlideData & EditableSlideProps & { plot?: StoredChoiceSlide['plot']; locale?: string | null }) {
  // D17: forwarded to every clamp that offers a truncation tooltip. In a static export
  // (the headless-browser PDF pass) a Radix trigger can never open, so those clamps fall
  // back to the native `title`, which is the only carrier a printed page can hold.
  const { editable, onEdit, renderMode } = props
  const resolvedActionTitle = sanitizeNarrativeText(props.actionTitle)
  const logoUrl = props.clientLogoUrl ?? null
  const bars = clampArray(props.bars || [], 'single-select-bars', 'bars')
  // A chart split by one computed cut draws its groups in place of the bars
  // (cuts throughout, Step 4); the gear still requests the whole question.
  const split =
    props.segmentSplit && props.segmentRows?.length
      ? { rows: clampArray(props.segmentRows, 'single-select-bars', 'segmentRows'), split: props.segmentSplit }
      : null
  const quote = props.quoteCard
  // One or two bullets take the column's roomier rhythm (see insightBulletRhythm).
  const bulletRhythm = insightBulletRhythm(props.insightBullets?.length ?? 0)
  // Dense charts (6-10 bars) compact the row spacing/labels so all bars fit the fixed panel.
  const dense = bars.length > 5

  return (
    <RailLayout
      footerText={props.footerText}
      preset={SINGLE_SELECT_SLIDE_TYPE}
      eyebrow={props.eyebrow || LEGACY_EYEBROW}
      clientLogoUrl={logoUrl}
      testIds={{ layout: 'single-select-bars-layout', plot: 'single-select-bars-panel', rail: 'single-select-bars-narrative' }}
      plotStyle={{
        ...PLOT_CARD_SURFACE,
        boxSizing: 'border-box',
        // A split ships whole: content past its budget stays visible (the builder warns).
        overflow: split ? 'visible' : 'hidden',
      }}
      railStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
      plot={
          <PlotBox
            slide={props as unknown as Record<string, unknown>}
            slideType="single-select-bars"
            columns={bars.map((bar) => ({ id: bar.id, label: bar.label }))}
            title={props.chartTitle || props.title || ''}
            showsSplit={split !== null}
            renderRecordedFigure={(source) => <RecordedChoiceSlideBars source={source} title={props.chartTitle || props.title || ''} testIdPrefix="single-select" renderMode={renderMode} plotOtherToggleLabels={props.plotOtherToggleLabels} />}
          >
          {split ? (
            renderCutSplit({
              slideType: 'single-select-bars',
              chartTitle: props.chartTitle || props.title || '',
              rows: split.rows,
              split: split.split,
            })
          ) : (
          <div
            style={{
              height: '100%',
              padding: '40px 32px',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
          {/* The heading is the question, whole: it wraps up to the schema's line budget. */}
          <ChartSegmentTitleSlot contentPadRight={32} style={{ flexShrink: 0 }}>
          <EditableSlideText
            data-testid="single-select-bars-chart-title"
            value={props.chartTitle || props.title || ''}
            field="chartTitle"
            editable={editable}
            onEdit={onEdit}
            tooltipOnTruncate
            renderMode={renderMode}
            minLines={CHART_TITLE_CLAMP.minLines}
            maxLines={CHART_TITLE_CLAMP.maxLines}
            lineHeight={CHART_TITLE_CLAMP.lineHeight}
            style={{ fontSize: 16, fontWeight: 600, color: 'var(--slide-text-primary)' }}
          />
          {/* A deck built before the heading became the question stored a chrome
              heading in chartTitle and the question in title: print the question under
              it. New decks store the question in both fields, so this line stays off. */}
          {props.title && props.chartTitle && props.title !== props.chartTitle ? (
            <TextClamp
              data-testid="single-select-bars-question-detail"
              minLines={1}
              maxLines={4}
              lineHeight="1.3"
              tooltipOnTruncate
              renderMode={renderMode}
              title={props.title}
              style={{ fontSize: 13, fontWeight: 400, color: GRAY, marginTop: 8 }}
            >
              {props.title}
            </TextClamp>
          ) : null}
          </ChartSegmentTitleSlot>
          <ChartHeadingGap height={dense ? 28 : 66} minHeight={dense ? 12 : 20} />

          <ChoiceSlideBars
            slide={{
              type: 'single-select-bars',
              bars,
              displayLabels: (props as { displayLabels?: StoredChoiceSlide['displayLabels'] }).displayLabels,
              plot: props.plot,
            }}
            testIdPrefix="single-select"
            locale={props.locale}
            editable={editable}
            onEdit={onEdit}
            renderMode={renderMode}
            plotOtherToggleLabels={props.plotOtherToggleLabels}
            plotOtherMembersHeading={props.plotOtherMembersHeading}
          />
          </div>
          )}
          </PlotBox>
      }
    >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 36, flexShrink: 0, minWidth: 0 }}>
            <SlotHeadline text={resolvedActionTitle || ''} maxLines={HEADLINE.maxLines} editable={editable} onEdit={onEdit} />
            {props.actionSubtitle ? (
              <SlotNarrative
                value={props.actionSubtitle}
                clamp={ACTION_SUBTITLE_CLAMP}
                testId="single-select-bars-action-subtitle"
                editable={editable}
                onEdit={onEdit}
                renderMode={renderMode}
              />
            ) : null}
            {props.insightBullets?.length ? (
              <SlotBullets
                bullets={props.insightBullets}
                fontSize={bulletRhythm.fontSize}
                gap={bulletRhythm.gap}
                maxLines={bulletRhythm.maxLines}
                dotColor={BRAND_EMPHASIS}
                testId="single-select-bars-insight-bullets"
                editable={editable}
                onEdit={onEdit}
                renderMode={renderMode}
              />
            ) : null}
          </div>

          {quote ? (
            <SlideQuoteCard quoteCard={quote} testId="single-select-bars-quote-card" />
          ) : null}
    </RailLayout>
  )
}

export const SingleSelectBarsSlide = registerSlide('single-select-bars', SingleSelectBarsSlideComponent)
